import React, { useEffect, useRef } from "react";
// Import GSAP and ScrollTrigger
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import TestimonialsSection from "./TestimonialsSection"; // Assuming this is imported for structure, though unused in the provided code

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const MissionSection = () => {
  // 1. Create refs for the section, image, and content card
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    // 2. Set up GSAP context
    const context = gsap.context(() => {
      // Define the common ScrollTrigger settings for the section's overall entry
      const triggerSettings = {
        trigger: sectionRef.current,
        start: "top 80%", // Animation starts when section hits 80% down the viewport
        end: "center 50%", // Animation completes by the time the section is near the center
        scrub: 1.5, // Smooth scrubbing effect
        // markers: true, // Uncomment for debugging
      };

      // ----------------------------------------------------
      // A. Image Animation (Slide in from Right)
      // ----------------------------------------------------
      if (imageRef.current) {
        gsap.from(imageRef.current, {
          x: 100, // Start 100px off to the right
          opacity: 0,
          scale: 0.95,
          ease: "power2.out",
          scrollTrigger: triggerSettings,
        });
      }

      // ----------------------------------------------------
      // B. Content Card Animation (Slide in from Left)
      // ----------------------------------------------------
      if (contentRef.current) {
        gsap.from(contentRef.current, {
          x: -100, // Start 100px off to the left
          opacity: 0,
          rotation: -1, // Subtle rotation for interest
          ease: "power2.out",
          scrollTrigger: triggerSettings,
        });
      }

      // ----------------------------------------------------
      // C. Inner Text Reveal Animation (Staggered Reveal)
      // ----------------------------------------------------
      const textElements = gsap.utils.toArray(
        contentRef.current.querySelectorAll(".mission-text-item"),
      );

      const innerTriggerSettings = {
        trigger: contentRef.current, // Use the content card as the trigger
        start: "top 85%", // Start when the card itself is clearly in view
        end: "center 60%",
        scrub: 1,
      };

      textElements.forEach((element, index) => {
        // Initial State: Hidden (opacity: 0), slightly down (y: 20), clipped
        gsap.set(element, {
          y: 20,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        gsap.to(element, {
          y: 0, // Final state: Original position (move up)
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)", // Fully visible
          duration: 1,
          ease: "power2.out",
          delay: index * 0.1, // Stagger effect for title and paragraph
          scrollTrigger: innerTriggerSettings,
        });
      });
    }, sectionRef);

    // 3. Cleanup
    return () => context.revert();
  }, []);

  return (
    <section
      ref={sectionRef} // Attach section ref
      className="w-full px-[30px] xl:px-[150px] mt-[50px] xl:mt-[400px]"
    >
      <div className="relative w-full">
        {/* Background Image - Attach image ref */}
        <img
          ref={imageRef}
          src="/assets/AboutPageImg/OurMission.png"
          className="relative xl:absolute top-[50px] xl:top-[-300px] right-0 w-[100%] xl:w-[52%] rounded-[20px] ml-auto "
          alt="Our Mission background"
        />

        {/* Content Overlay - Attach content ref */}
        <div
          ref={contentRef}
          className="relative z-[99999] w-[100%] xl:w-[56%] bg-[#1C233A0D] backdrop-blur-xl shadow-xl rounded-[20px] px-[20px] xl:px-[58px] py-[20px] xl:py-[54px]"
        >
          {/* Header - Added mission-text-item class */}
          <h2 className="mission-text-item text-[30px] xl:text-[100px] font-bold text-secondary-background font-['Parkinsans']">
            Our Mission
          </h2>

          {/* Paragraph - Added mission-text-item class */}
          <p className="mission-text-item text-[18px] xl:text-[24px] text-secondary-background mt-1 xl:mt-4 font-light font-['Poppins'] text-justify">
            Our mission at UTPANN (Govardhannathji Energies LLP) is to convert
            organic waste into valuable resources through advanced biogas
            technology. We are committed to producing clean energy and
            high-quality organic fertilizers that support sustainable
            agriculture, protect public health, and reduce environmental impact.
            By promoting responsible waste management and circular resource use,
            we aim to drive long-term sustainable development and contribute
            meaningfully to India’s clean energy and soil regeneration goals.
          </p>
        </div>
      </div>
    </section>
  );
};

export default MissionSection;
